import { useState, useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';

/**
 * Custom hook that provides local state for immediate UI feedback
 * while debouncing updates to a parent state setter for string values
 * 
 * @param propValue - The string value from parent state
 * @param setState - Parent state setter function (supports callback form)
 * @param fieldName - Name of the field to update in parent state
 * @param delay - Debounce delay in milliseconds (default: 300)
 * @returns [localValue, handleChange] - Local value and change handler
 */
export function useDebouncedStateString<T, K extends keyof T>(
  propValue: string,
  setState: (state: T | ((prevState: T) => T)) => void,
  fieldName: K,
  delay: number = 300
): [string, (value: string) => void] {
  // Local state for immediate UI feedback
  const [localValue, setLocalValue] = useState(propValue || '');

  // Sync local state when prop value changes
  useEffect(() => {
    setLocalValue(propValue || '');
  }, [propValue]);

  // Debounced state update to reduce re-renders and API calls
  const debouncedSetState = useRef(
    debounce((newValue: string) => {
      setState((prevState) => ({ ...prevState, [fieldName]: newValue } as T));
    }, delay)
  ).current;

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedSetState.cancel();
    };
  }, [debouncedSetState]);

  // Change handler that updates local state immediately and debounces parent update
  const handleChange = (value: string) => {
    setLocalValue(value);
    debouncedSetState(value);
  };

  return [localValue, handleChange];
}

export default useDebouncedStateString;
